/**
 * La situation d'un lecteur, calculée depuis son dossier.
 *
 * AUCUN CHIFFRE N'EST CALCULÉ ICI, ILS LE SONT TOUS PAR LES OUTILS. Ce module
 * appelle les moteurs que les outils utilisent déjà, et que leurs contrôles
 * vérifient : revenuNet, statutTva, statutMicro, comparerRegimes,
 * meilleurPartage, echeancesPremiereAnnee, echeancesSociete. Un barème qui
 * change dans un outil change donc ici le même jour, sans rien toucher.
 *
 * IL NE REND QUE DES DONNÉES, jamais de phrases : les mots sont ceux de la
 * page (components/Tools/ToolsSituation.jsx), dans sa langue.
 *
 * LA PAGE NE PARLE QUE DE CE QU'ELLE SAIT. Sans chiffre d'affaires, pas de
 * revenu net ni de TVA ; sans statut, pas de comparaison de statuts ; sans
 * date de début, pas d'échéance de première année. Chaque bloc manquant se
 * dit comme une question à compléter, pas comme un zéro.
 *
 * ET UN PASSAGE AU CONDITIONNEL QUAND IL LE FAUT. Le versement libératoire
 * dépend du revenu fiscal de référence du foyer, que le dossier ne demande
 * pas : la page en donne le gain, et la condition.
 */

import { PLAFONDS, revenuNet, statutMicro, statutTva } from "./microEnterprise.js";
import { comparerRegimes, scenarioReel } from "./businessStructure.js";
import { comparerFormes, meilleurPartage } from "./salaryDividends.js";
import { echeancesPremiereAnnee } from "./deadlines.js";
import { echeancesSociete } from "./companyDeadlines.js";
import { economieAcre, finAcre, joursAvantEcheance, limiteDemandeAcre } from "./incomeTax.js";
import { moisDeLAnnee, nettoyerDossier, rythmeDesEncaissements } from "./dossier.js";

/**
 * Aujourd'hui à Paris, en AAAA-MM-JJ : c'est là que tombent les échéances.
 * Une seule définition pour la page, l'agenda et les e-mails d'alerte, qui ne
 * doivent jamais être en désaccord sur la date du jour.
 */
export function aujourdhuiAParis(maintenant = new Date()) {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris" }).format(maintenant);
}

/** En dessous, un écart ne mérite pas qu'on change quoi que ce soit. */
export const ECART_UTILE = 1000;
/** En dessous, le gain du versement libératoire ne vaut pas une démarche. */
export const GAIN_VFL_UTILE = 150;
/** En dessous, l'impôt de la première année ne mérite pas d'ouvrir un acompte. */
export const IMPOT_A_ETALER = 300;

const JOUR = 86400000;

function dateMidi(iso) {
    if (!iso) return null;
    const [a, m, j] = iso.slice(0, 10).split("-").map(Number);
    const d = new Date(a, m - 1, j, 12, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? null : d;
}

function iso(d) {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function joursEntre(de, a) {
    return Math.ceil((a - de) / JOUR);
}

/**
 * Le jour où un rythme constant fait passer un seuil : le même calcul que la
 * carte de l'accueil (jourDuFranchissement, lib/tools/situation.js), recopié
 * parce que ce module doit se charger seul dans les contrôles.
 */
function jourDuFranchissement(seuil, caAnnuel, debutDecompte) {
    const jours = Math.ceil((seuil * 365) / caAnnuel);
    const d = new Date(debutDecompte);
    d.setDate(d.getDate() + jours);
    d.setHours(12, 0, 0, 0);
    return d;
}

/**
 * Le jour où les vrais encaissements passent un seuil.
 *
 * Mois par mois, le cumul de l'année : le mois où il dépasse le seuil, le jour
 * se place au prorata de ce qu'il restait à encaisser dans ce mois. Pas
 * encore dépassé : on part de la fin du dernier mois donné, au rythme des
 * mois couverts. Ce n'est plus une moyenne sur l'année, c'est le calendrier
 * de ce que le lecteur a vraiment encaissé.
 */
function jourDuFranchissementReel(seuil, encaissements, annee, rythme) {
    let cumul = 0;
    for (const { mois, montant } of encaissements) {
        if (montant > 0 && cumul + montant > seuil) {
            const [a, m] = mois.split("-").map(Number);
            const joursDuMois = new Date(a, m, 0).getDate();
            const jour = Math.max(1, Math.ceil(((seuil - cumul) / montant) * joursDuMois));
            return new Date(a, m - 1, jour, 12, 0, 0, 0);
        }
        cumul += montant;
    }
    if (!(rythme > 0)) return null;
    const dernier = encaissements[encaissements.length - 1].mois;
    const [a, m] = dernier.split("-").map(Number);
    const finDuMois = new Date(a, m, 0, 12, 0, 0, 0);
    const d = jourDuFranchissement(seuil - cumul, rythme, finDuMois);
    // Au-delà du 31 décembre, le seuil n'est pas passé cette année.
    return d.getFullYear() > annee ? null : d;
}

/** La TVA : vente au seuil global, tout le reste au seuil des prestations. */
const CATEGORIE_TVA = { vente: "global", services: "services", bnc: "services", cipav: "services" };

/** L'activité sous les noms des moteurs de société. */
function activiteSociete(activite) {
    return activite === "vente" ? "services" : activite;
}

/**
 * Tout ce que la page affiche, calculé une fois.
 *
 * @returns {{
 *   dossier, aujourdhui,
 *   manque: string[],               les questions sans réponse
 *   revenu: null | { annuel, mensuel, cotisations, impot, cfp, frais, objectif, ecartMensuel, source },
 *   tva: null | { statut, base, majore, date, passe, joursRestants, soins, reduit },
 *   plafond: null | { plafond, ca, part, acquis, fermeture },
 *   vfl: null | { option, gain, limite, joursRestants, creation },
 *   acre: null | { obtenue, fin, limiteDemande, joursDemande, cetteAnnee, enCours, economie },
 *   statutConseil: null | { type, meilleur, gain, nets },
 *   echeances: { cle, date, outil, famille }[],
 *   alertes: { cle, gravite, outil }[],
 * }}
 */
export function situationDuDossier(brut, aujourdhuiIso) {
    const d = nettoyerDossier(brut);
    const aujourdhui = dateMidi(aujourdhuiIso || aujourdhuiAParis());
    const annee = aujourdhui.getFullYear();
    const debut = dateMidi(d.dateDebut);
    const statut = d.statut;
    const enMicro = statut === "micro" || statut === "projet";
    const foyer = {
        enCouple: Boolean(d.enCouple),
        enfants: d.enfants || 0,
        autresRevenus: d.autresRevenus || 0,
    };
    const ca = d.caCetteAnnee;
    const fraisAnnuels = (d.fraisMensuels || 0) * 12;

    const manque = [];
    if (!statut) manque.push("statut");
    if (!d.activite) manque.push("activite");
    if (!debut && statut !== "projet") manque.push("dateDebut");
    if (ca === null) manque.push("caCetteAnnee");
    if (d.enCouple === null || d.enfants === null) manque.push("foyer");
    if (d.objectifMensuel === null) manque.push("objectifMensuel");

    const r = {
        dossier: d,
        aujourdhui: iso(aujourdhui),
        manque,
        revenu: null,
        tva: null,
        plafond: null,
        vfl: null,
        statutConseil: null,
        echeances: [],
        alertes: [],
        // Les démarches déclarées faites : elles sortent des alertes et des
        // échéances, et s'affichent à part, « En ordre ».
        enOrdre: [],
        // Le chiffre d'affaires qui vieillit (voir plus bas).
        caAncien: null,
    };

    // ------------------------------------------------------------------
    // L'ACRE : ses dates se lisent dès qu'on connaît le début d'activité
    // ------------------------------------------------------------------
    // finAcre et limiteDemandeAcre travaillent en UTC, la page à midi, heure
    // de Paris : on passe de l'un à l'autre par le jour du calendrier.
    const versUtc = (x) => new Date(Date.UTC(x.getFullYear(), x.getMonth(), x.getDate()));
    const versMidi = (x) => new Date(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate(), 12);
    r.acre = null;
    if (enMicro && debut) {
        const fin = versMidi(finAcre(versUtc(debut)));
        const limite = versMidi(limiteDemandeAcre(versUtc(debut)));
        r.acre = {
            obtenue: d.acre === true,
            fin: iso(fin),
            limiteDemande: iso(limite),
            joursDemande: joursEntre(aujourdhui, limite),
            // L'exonération touche-t-elle encore les cotisations de cette année ?
            cetteAnnee: fin.getFullYear() >= annee,
            enCours: fin >= aujourdhui,
            economie: 0,
        };
    }

    // ------------------------------------------------------------------
    // Ce qu'il reste
    // ------------------------------------------------------------------
    if (ca !== null && d.activite) {
        if (enMicro || !statut) {
            const n = revenuNet({
                chiffreAffaires: ca,
                activite: d.activite,
                artisan: Boolean(d.artisan),
                optionFiscale: d.optionFiscale || "bareme",
                ...foyer,
            });
            // L'année de création ne paie pas la contribution à la formation.
            const premiereAnnee = statut === "projet" || (debut && debut.getFullYear() === annee);
            // L'ACRE réduit les cotisations des jours de l'année qu'elle
            // couvre. Le chiffre d'affaires est un rythme annuel : ces jours
            // en encaissent leur part (economieAcre, le moteur du simulateur).
            let acre = 0;
            if (r.acre?.obtenue && r.acre.cetteAnnee) {
                const janvier = new Date(annee, 0, 1, 12);
                const de = debut > janvier ? debut : janvier;
                const fin = dateMidi(r.acre.fin);
                const a = fin < new Date(annee, 11, 31, 12) ? fin : new Date(annee, 11, 31, 12);
                const jours = a >= de ? joursEntre(de, a) + 1 : 0;
                if (jours > 0) acre = economieAcre({ chiffreAffaires: (ca * jours) / 365, activite: d.activite, dateDebut: versUtc(debut) }).economie;
                r.acre.economie = acre;
            }
            const annuel = (premiereAnnee ? n.net + n.cfp : n.net) + acre;
            r.revenu = {
                source: "micro",
                annuel,
                cotisations: n.cotisations - acre,
                cfp: premiereAnnee ? 0 : n.cfp,
                impot: n.impot,
                frais: fraisAnnuels,
                acre,
                netAutreOption: (premiereAnnee ? n.netAutreOption + n.cfp : n.netAutreOption) + acre,
            };
        } else if ((statut === "reel" || statut === "eurlIr") && d.activite !== "cipav") {
            const s = scenarioReel({
                chiffreAffaires: ca,
                charges: fraisAnnuels,
                activite: d.activite,
                artisan: Boolean(d.artisan),
                foyer,
            });
            // Au réel, les frais sont déjà déduits du bénéfice.
            r.revenu = { source: "reel", annuel: s.net + fraisAnnuels, cotisations: s.cotisations?.total ?? s.cotisations, impot: s.impot, cfp: 0, frais: fraisAnnuels };
        } else if ((statut === "eurl" && d.activite !== "cipav") || statut === "sasu") {
            const m = meilleurPartage({
                forme: statut,
                enveloppe: Math.max(0, ca - fraisAnnuels),
                activite: activiteSociete(d.activite),
                artisan: Boolean(d.artisan),
                foyer,
            });
            r.revenu = {
                source: "societe",
                annuel: m.meilleur.net + fraisAnnuels,
                cotisations: null,
                impot: null,
                // Cotisations, impôt sur les sociétés et impôt sur le revenu,
                // pour le meilleur partage : le total, faute de détail comparable.
                prelevements: m.meilleur.prelevements,
                cfp: 0,
                frais: fraisAnnuels,
                partageUtile: m.partageUtile,
            };
        }
        if (r.revenu) {
            // Ce qui reste pour vivre : le net moins les frais payés de sa poche.
            r.revenu.mensuel = r.revenu.annuel / 12;
            r.revenu.disponibleMensuel = (r.revenu.annuel - fraisAnnuels) / 12;
            r.revenu.objectif = d.objectifMensuel;
            r.revenu.ecartMensuel = d.objectifMensuel !== null ? r.revenu.disponibleMensuel - d.objectifMensuel : null;
            if (r.revenu.ecartMensuel !== null && r.revenu.ecartMensuel < -50) {
                r.alertes.push({ cle: "objectif", gravite: "info", outil: "combien-facturer" });
            }
        }
    }

    // ------------------------------------------------------------------
    // La TVA : elle vaut pour tous les statuts
    // ------------------------------------------------------------------
    if (ca !== null && d.activite && statut !== "projet") {
        const categorie = CATEGORIE_TVA[d.activite];
        const debutCetteAnnee = debut && debut.getFullYear() === annee ? debut : null;
        const debutAnDernier = debut && debut.getFullYear() === annee - 1 ? debut : null;
        // L'année de création, le chiffre de l'année ne couvre que ses jours
        // d'activité : un rythme annuel de 48 000 € commencé en juillet n'en
        // encaissera que la moitié.
        const joursActifs = debutCetteAnnee ? joursEntre(debutCetteAnnee, new Date(annee, 11, 31, 12)) + 1 : 365;
        const caCetteAnnee = (ca * joursActifs) / 365;
        const t = statutTva({
            categorie,
            caN1: debut && debut.getFullYear() < annee ? d.caAnDernier || 0 : 0,
            caEnCours: caCetteAnnee,
            debutN1: debutAnDernier ? iso(debutAnDernier) : null,
            debutEnCours: debutCetteAnnee ? iso(debutCetteAnnee) : null,
        });
        const soins = d.activite === "cipav";
        const tva = {
            statut: t.statut,
            base: t.base,
            majore: t.majore,
            reduit: Boolean(t.prorata?.applique),
            soins,
            date: null,
            passe: false,
            joursRestants: null,
            // Vrai quand la date vient des mois encaissés, et non d'une moyenne.
            depuisEncaissements: false,
        };
        if (t.statut === "assujetti_immediat") {
            const decompte = debutCetteAnnee || new Date(annee, 0, 1, 12);
            // Avec les mois encaissés, la date vient du calendrier réel.
            const reel = rythmeDesEncaissements(d.encaissements, annee, d.dateDebut);
            const mois = reel ? moisDeLAnnee(annee, Number(reel.dernier.slice(5, 7)), d.dateDebut) : [];
            const jourReel = reel
                ? jourDuFranchissementReel(t.majore, mois.map((m) => ({ mois: m, montant: d.encaissements[m] || 0 })), annee, reel.rythme)
                : null;
            tva.depuisEncaissements = Boolean(jourReel);
            const jour = jourReel || jourDuFranchissement(t.majore, ca, decompte);
            tva.date = iso(jour);
            tva.passe = jour < aujourdhui;
            tva.joursRestants = joursEntre(aujourdhui, jour);
        } else if (t.statut === "bascule_janvier_prochain") {
            const effet = new Date(annee + 1, 0, 1, 12);
            tva.date = iso(effet);
            tva.joursRestants = joursEntre(aujourdhui, effet);
        } else if (t.statut === "assujetti_depuis_janvier") {
            // L'an dernier a dépassé le seuil : la TVA est due depuis le
            // 1er janvier de cette année, quel que soit le rythme actuel.
            const effet = new Date(annee, 0, 1, 12);
            tva.date = iso(effet);
            tva.passe = true;
            tva.joursRestants = joursEntre(aujourdhui, effet);
        }
        // DÉJÀ À LA TVA, DE SON PROPRE AVEU (option, ou seuil passé une
        // année précédente) : plus rien à annoncer, et aucune alerte. Le seuil
        // reste connu, pour qui voudrait revenir en franchise.
        if (d.tvaFacturee === true) {
            r.tva = { ...tva, statut: "deja", date: null, passe: false, joursRestants: null };
        } else r.tva = tva;
        if (d.tvaFacturee === true) {
            // Rien : la TVA est déjà là.
        } else if (t.statut === "assujetti_immediat" || t.statut === "assujetti_depuis_janvier") {
            const loin = t.statut === "assujetti_immediat" && tva.joursRestants > 60;
            r.alertes.push({ cle: "tva", gravite: soins || loin ? "attention" : "urgent", outil: "seuils-tva" });
        } else if (t.statut === "bascule_janvier_prochain") {
            r.alertes.push({ cle: "tva", gravite: "attention", outil: "seuils-tva" });
        } else if (t.statut === "franchise" && caCetteAnnee >= t.base * 0.8) {
            r.alertes.push({ cle: "tva", gravite: "info", outil: "seuils-tva" });
        }
    }

    // ------------------------------------------------------------------
    // Le plafond de la micro
    // ------------------------------------------------------------------
    if (enMicro && ca !== null && d.activite && PLAFONDS[d.activite]) {
        const plafond = PLAFONDS[d.activite];
        const debutAnnee = debut ? debut.getFullYear() : null;
        const anciennete =
            statut === "projet" || debutAnnee === annee
                ? "premiere"
                : debutAnnee === annee - 1
                  ? "deuxieme"
                  : "etablie";
        const m = statutMicro({
            activite: d.activite,
            anciennete,
            caN1: ca,
            caN2: d.caAnDernier || 0,
        });
        r.plafond = {
            plafond,
            ca,
            part: ca / plafond,
            acquis: m.acquis,
            // Deux années de suite au-dessus du plafond : la micro se ferme
            // au 1er janvier qui suit.
            fermeture: !m.acquis && ca > plafond && (d.caAnDernier || 0) > plafond,
        };
        if (r.plafond.fermeture) r.alertes.push({ cle: "plafond", gravite: "urgent", outil: "plafonds" });
        else if (r.plafond.part >= 0.8) r.alertes.push({ cle: "plafond", gravite: "attention", outil: "plafonds" });
    }

    // ------------------------------------------------------------------
    // Le versement libératoire
    // ------------------------------------------------------------------
    if (enMicro && r.revenu?.source === "micro" && d.activite !== "cipav") {
        const option = d.optionFiscale || "bareme";
        const gain = r.revenu.netAutreOption - r.revenu.annuel;
        // L'année de création, l'option se prend jusqu'à la fin du troisième
        // mois d'activité ; ensuite, avant le 30 septembre pour l'an prochain.
        let limite = null;
        // Un projet sans date : l'option se prendra dans les trois mois de la
        // création, il n'y a pas encore de date à donner.
        let creation = statut === "projet" && !debut;
        if (debut && debut.getFullYear() >= annee) {
            const e = echeancesPremiereAnnee({ dateDebut: debut, aujourdhui }).find((x) => x.cle === "vfl-option");
            if (e && !e.passee) {
                limite = e.date;
                creation = true;
            }
        }
        if (!limite && !creation) {
            const pourAnnee = joursAvantEcheance(annee + 1, aujourdhui) >= 0 ? annee + 1 : annee + 2;
            limite = new Date(pourAnnee - 1, 8, 30, 12);
        }
        r.vfl = {
            option,
            gain,
            limite: limite ? iso(limite) : null,
            joursRestants: limite ? joursEntre(aujourdhui, limite) : null,
            creation,
            pourAnnee: creation ? (debut ? debut.getFullYear() : null) : limite.getFullYear() + 1,
        };
        if (gain >= GAIN_VFL_UTILE) {
            r.alertes.push({
                cle: option === "vfl" ? "vfl_renoncer" : "vfl",
                // La démarche qu'on peut déclarer faite : la même clé que son
                // échéance, pour que l'une et l'autre disparaissent ensemble.
                fait:
                    limite &&
                    `${option === "vfl" ? "vfl-renoncer" : creation ? "vfl-option" : "vfl-option-annuelle"}@${iso(limite)}`,
                gravite: r.vfl.joursRestants !== null && r.vfl.joursRestants <= 30 ? "urgent" : "info",
                outil: "versement-liberatoire",
            });
        }
    }

    // ------------------------------------------------------------------
    // L'impôt de la première année
    // ------------------------------------------------------------------
    // Au barème, rien n'est prélevé sur une activité nouvelle tant que les
    // impôts ne la connaissent pas : l'année de création, et la suivante
    // jusqu'en août, car les acomptes ne démarrent qu'en septembre, après la
    // première déclaration. Les impôts conseillent eux-mêmes d'ouvrir un
    // acompte dès le début (espace particulier, « Gérer mon prélèvement à la
    // source », puis « Gérer vos acomptes ») : il est alors prélevé chaque
    // mois. Un acompte déclaré ouvert une année précédente vaut pour la suite.
    const deuxiemeAnnee = Boolean(debut && debut.getFullYear() === annee - 1 && aujourdhui.getMonth() <= 7);
    const acompteDejaOuvert = Object.keys(d.faits || {}).some((k) => k.startsWith("impot-acomptes@") && k !== `impot-acomptes@${annee}-01-01`);
    if (
        enMicro &&
        r.revenu?.source === "micro" &&
        (d.optionFiscale || "bareme") !== "vfl" &&
        r.revenu.impot >= IMPOT_A_ETALER &&
        !acompteDejaOuvert &&
        (statut === "projet" || (debut && debut.getFullYear() === annee) || deuxiemeAnnee)
    ) {
        // Un projet n'a pas encore d'espace où ouvrir l'acompte : pas de « C'est fait ».
        r.alertes.push({ cle: "impot_acomptes", gravite: "info", outil: "revenu-net", deuxiemeAnnee, fait: statut === "projet" ? null : `impot-acomptes@${annee}-01-01` });
    }

    // ------------------------------------------------------------------
    // Changer de statut ?
    // ------------------------------------------------------------------
    if (ca !== null && d.activite && d.activite !== "cipav" && (enMicro || statut === "reel")) {
        const c = comparerRegimes({
            chiffreAffaires: ca,
            charges: fraisAnnuels,
            activite: d.activite,
            enCouple: foyer.enCouple,
            enfants: foyer.enfants,
            autresRevenus: foyer.autresRevenus,
        });
        const actuel = enMicro ? "micro" : "reel";
        const net = (regime) => c.scenarios.find((s) => s.regime === regime)?.net ?? null;
        const meilleur = c.meilleurRegime;
        const gain = net(meilleur) - net(actuel);
        r.statutConseil = {
            actuel,
            meilleur,
            gain,
            type: meilleur !== actuel && gain >= ECART_UTILE ? "changer" : "garder",
            second: [...c.scenarios].filter((s) => s.regime !== actuel).sort((a, b) => b.net - a.net)[0]?.regime ?? null,
            nets: Object.fromEntries(c.scenarios.map((s) => [s.regime, s.net])),
        };
        if (r.statutConseil.type === "changer") {
            r.alertes.push({ cle: "statut", gravite: "attention", outil: "micro-reel-ou-societe" });
        }
    } else if (ca !== null && d.activite && (statut === "eurl" || statut === "sasu") && d.activite !== "cipav") {
        const f = comparerFormes({
            enveloppe: Math.max(0, ca - fraisAnnuels),
            activite: activiteSociete(d.activite),
            artisan: Boolean(d.artisan),
            foyer,
        });
        const autre = statut === "eurl" ? "sasu" : "eurl";
        const netDe = (forme) => (forme === "eurl" ? f.eurl?.meilleur?.net : f.sasu?.meilleur?.net) ?? null;
        const gain = netDe(autre) !== null && netDe(statut) !== null ? netDe(autre) - netDe(statut) : 0;
        r.statutConseil = {
            actuel: statut,
            meilleur: gain >= ECART_UTILE ? autre : statut,
            gain,
            type: gain >= ECART_UTILE ? "changer" : "garder",
            second: autre,
            nets: { [statut]: netDe(statut), [autre]: netDe(autre) },
        };
    }

    // ------------------------------------------------------------------
    // Les échéances des douze prochains mois
    // ------------------------------------------------------------------
    const horizon = new Date(aujourdhui.getTime() + 366 * JOUR);
    const ajouter = (cle, date, outil, famille) => {
        if (date >= aujourdhui && date <= horizon) {
            r.echeances.push({ cle, date: iso(date), outil, famille, joursRestants: joursEntre(aujourdhui, date), fait: `${cle}@${iso(date)}` });
        }
    };

    if (enMicro) {
        if (debut) {
            for (const e of echeancesPremiereAnnee({ dateDebut: debut, aujourdhui })) {
                if (!e.passee) ajouter(e.cle, e.date, e.outil, "micro");
            }
        }
        // La CFE revient chaque 15 décembre. La première, l'année qui suit la
        // création, est déjà dans les échéances de première année.
        if (statut === "micro") {
            for (const y of [annee, annee + 1]) {
                if (!debut || y >= debut.getFullYear() + 2) {
                    ajouter("cfe-solde", new Date(y, 11, 15, 12), "reclamation-cfe", "micro");
                }
            }
        }
        if (r.vfl && r.vfl.gain >= GAIN_VFL_UTILE && !r.vfl.creation && r.vfl.limite) {
            ajouter(r.vfl.option === "vfl" ? "vfl-renoncer" : "vfl-option-annuelle", dateMidi(r.vfl.limite), "versement-liberatoire", "micro");
        }
    } else if (statut === "eurl" || statut === "sasu") {
        const e = echeancesSociete({
            forme: statut,
            prochaineCloture: `${annee}-12-31`,
            premierExercice: Boolean(debut && debut.getFullYear() === annee),
            dateCreation: d.dateDebut,
            isReference: "inconnu",
            tva: r.tva && r.tva.statut !== "franchise" ? "simplifie" : "franchise",
            salaire: statut === "sasu",
            cotisations: "5",
            dividendes: false,
            aujourdhui: r.aujourdhui,
        });
        for (const x of e.ponctuelles) {
            if (x.incertain) continue;
            const jour = new Date(x.date.getUTCFullYear(), x.date.getUTCMonth(), x.date.getUTCDate(), 12);
            ajouter(x.cle, jour, "echeances-societe", "societe");
        }
    }

    // Une date proche fait une alerte à elle seule.
    for (const e of r.echeances) {
        // Le versement libératoire a déjà sa propre alerte, avec son gain.
        if (e.joursRestants <= 30 && ["acre-demande", "cfe-declaration"].includes(e.cle)) {
            r.alertes.push({ cle: `echeance_${e.cle}`, gravite: "urgent", outil: e.outil, echeance: e, fait: e.fait });
        }
    }

    // LES DÉMARCHES FAITES SORTENT DE LA LISTE. Une ACRE demandée, une CFE
    // déclarée, un versement libératoire choisi : le lecteur l'a dit, la carte
    // ne revient plus à chaque visite. Elle passe dans « En ordre », d'où il
    // peut la rappeler.
    const faits = d.faits || {};
    const estFait = (x) => x.fait && faits[x.fait];
    for (const e of r.echeances) {
        if (estFait(e)) r.enOrdre.push({ fait: e.fait, cle: e.cle, famille: e.famille, date: e.date, le: faits[e.fait] });
    }
    // L'acompte d'impôt n'a pas d'échéance : l'alerte seule passe en ordre.
    for (const a of r.alertes) {
        if (a.cle === "impot_acomptes" && estFait(a)) r.enOrdre.push({ fait: a.fait, cle: "impot-acomptes", famille: "micro", date: null, le: faits[a.fait] });
    }
    r.echeances = r.echeances.filter((e) => !estFait(e));
    r.alertes = r.alertes.filter((a) => !estFait(a));
    // Sans ACRE (le lecteur l'a dit), pas de « fin de l'ACRE » à annoncer.
    if (d.acre === false) r.echeances = r.echeances.filter((e) => e.cle !== "acre-fin");
    // Une ACRE déclarée obtenue n'est plus à demander.
    if (d.acre === true) {
        r.echeances = r.echeances.filter((e) => e.cle !== "acre-demande");
        r.alertes = r.alertes.filter((a) => a.echeance?.cle !== "acre-demande");
    }

    // LE CHIFFRE D'AFFAIRES QUI VIEILLIT. Toute la projection de TVA repose
    // sur un chiffre annuel tapé une fois : passé soixante jours, ou dès
    // qu'un trimestre a commencé depuis, la page demande s'il tient toujours.
    if (d.caCetteAnnee !== null && d.caLe) {
        const le = dateMidi(d.caLe);
        const jours = joursEntre(le, aujourdhui);
        const trimestre = (x) => x.getFullYear() * 4 + Math.floor(x.getMonth() / 3);
        if (jours > 60 || (jours > 14 && trimestre(aujourdhui) > trimestre(le))) {
            r.caAncien = { le: d.caLe, jours };
        }
    }

    r.echeances.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    const rang = { urgent: 0, attention: 1, info: 2 };
    r.alertes.sort((a, b) => rang[a.gravite] - rang[b.gravite]);
    return r;
}

/**
 * Les alertes que la page affiche en cartes. L'écart à l'objectif a sa place
 * dans la carte « Ce qu'il vous reste », pas en tête. L'entrée de /outils
 * compte sur cette même liste : les deux ne peuvent pas annoncer un nombre
 * différent.
 */
export function alertesAffichees(r) {
    return r.alertes.filter((a) => a.cle !== "objectif");
}

/**
 * Les outils à garder en vue pour ce dossier, dans l'ordre où ils servent.
 * Ce sont ceux dont un changement de règle changerait un chiffre de la page.
 */
export function outilsDuDossier(brut) {
    const d = nettoyerDossier(brut);
    const enMicro = d.statut === "micro" || d.statut === "projet" || d.statut === null;
    if (enMicro) {
        return [
            "revenu-net",
            "seuils-tva",
            "plafonds",
            "versement-liberatoire",
            "combien-facturer",
            "micro-reel-ou-societe",
            ...(d.statut === "projet" || (d.dateDebut && Number(d.dateDebut.slice(0, 4)) >= new Date().getFullYear()) ? ["acre"] : []),
        ];
    }
    if (d.statut === "reel" || d.statut === "eurlIr") {
        return ["micro-reel-ou-societe", "passer-a-l-is", "seuils-tva", "eurl-ou-sasu"];
    }
    return ["remuneration-ou-dividendes", "eurl-ou-sasu", "echeances-societe", "seuils-tva", "combien-me-verser"];
}
